import { Button, Input, Label, toast } from "@awtoban/ui";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { LocaleSwitcher } from "../../i18n/LocaleSwitcher";
import { useAuth } from "./AuthProvider";

/** Shown when the terminal isn't yet bound to a station. A manager (or HQ)
 * signs in once to bind it; operators then start shifts with a PIN. */
export function BindTerminalScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [stationCode, setStationCode] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await auth.bindTerminal(username, password, stationCode.trim() || undefined);
    } catch (err) {
      const reason = err instanceof Error ? err.message : "bind_failed";
      const hintKey = `bind.err_${reason}`;
      toast.error(t("bind.failed"), {
        description: t(hintKey, { defaultValue: t("bind.failed_hint") }),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-paper-soft px-6">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-md border border-line-strong bg-paper p-8 shadow-sm"
      >
        <div className="mb-6 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-semibold text-2xl tracking-tight">{t("bind.title")}</h1>
            <p className="mt-1 text-ink-muted text-sm">{t("bind.subtitle")}</p>
          </div>
          <div className="shrink-0">
            <LocaleSwitcher />
          </div>
        </div>

        <div className="mb-4">
          <Label htmlFor="u">{t("bind.manager_username")}</Label>
          <Input
            id="u"
            inputSize="lg"
            autoFocus
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>

        <div className="mb-4">
          <Label htmlFor="p">{t("auth.password")}</Label>
          <Input
            id="p"
            type="password"
            inputSize="lg"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div className="mb-6">
          <Label htmlFor="s">{t("bind.station_code")}</Label>
          <Input
            id="s"
            inputSize="lg"
            placeholder={t("bind.station_code_hint")}
            value={stationCode}
            onChange={(e) => setStationCode(e.target.value.toUpperCase())}
            className="tabular tracking-wider"
          />
        </div>

        <Button type="submit" size="lg" className="w-full" disabled={submitting}>
          {submitting ? t("bind.binding") : t("bind.button")}
        </Button>
      </form>
    </div>
  );
}
